// Batch onboarding for the NIST calibration account (iso@tempstick.com).
//
// Recalibration setup is nothing but "name = built-in ID, join the calibration
// Wi-Fi", and the lab does dozens at a time, so this runs the same per-sensor
// sequence the single flow does — /wlan, wait for status 9, /acc, a first
// reading, /restart, then the two API calls that make the sensor live on the
// account — over Bluetooth for every sensor in the list, with none of the
// per-sensor screens. Customers never see it: the entry point is gated on
// isNistAccount().
//
// Two passes, so the sensors test the Wi-Fi in parallel while the phone is
// busy with the others:
//   pass 1  connect, hand over the credentials, disconnect, next
//   pass 2  connect, read the verdict, register, restart, next
// The link is one sensor at a time (the app's plugin holds one connection),
// but the slow part — each sensor's own connect/internet/server test — runs
// on the sensors, all at once.
//
// Firmware assumptions, spelled out in firmware/esp32s3-ble-sim/HANDOFF.md: a
// sensor keeps advertising and keeps its Wi-Fi test running while no phone is
// connected, and /status answers after a reconnect.

import getToken from '../scripts/get-token'

export const BULK_STATE = {
  QUEUED: 'queued',
  SENDING: 'sending',        // pass 1: connecting / handing over credentials
  TESTING: 'testing',        // credentials delivered; sensor is testing on its own
  CHECKING: 'checking',      // pass 2: connecting / polling the verdict
  REGISTERING: 'registering',
  FINALIZING: 'finalizing',  // API calls
  DONE: 'done',
  FAILED: 'failed'
};

const STATUS_POLL_MS = 3000;
const STATUS_TIMEOUT_MS = 120000;
const CONNECT_TIMEOUT_MS = 15000;
const REQUEST_TIMEOUT_MS = 15000;

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

export class NistBulkOnboarding {

  constructor (onboarding, devices, wifi) {

    this.onboarding = onboarding;
    this.wifi = wifi;                // { ssid, password }
    this.onChange = () => {};
    this.running = false;
    this.cancelled = false;

    this.items = (devices || []).map(device => ({
      address: String(device.address || ''),
      sensorId: String(device.sensorId || ''),
      name: String(device.name || ''),
      state: BULK_STATE.QUEUED,
      message: '',
      readings: null,
      attempts: 0
    }));

  }

  summary() {

    const total = this.items.length;
    const done = this.items.filter(item => item.state === BULK_STATE.DONE).length;
    const failed = this.items.filter(item => item.state === BULK_STATE.FAILED).length;

    return { total, done, failed, running: this.running, finished: !this.running && (done + failed) === total };

  }

  setState(item, state, message = '') {
    item.state = state;
    item.message = message;
    this.notify();
  }

  notify() {
    try {
      this.onChange(this.items, this.summary());
    } catch (e) {
      console.error(e);
    }
  }

  cancel() {
    this.cancelled = true;
  }

  // ── The run ────────────────────────────────────────────────────────────────

  async run() {

    if (this.running) {
      return;
    }

    this.running = true;
    this.cancelled = false;
    this.onboarding.bulk_active = true;
    this.onboarding.is_trying_wifi = true;   // keeps the menu from navigating away mid-run
    this.onboarding.transport = 'ble';

    try {

      const pending = this.items.filter(item => item.state !== BULK_STATE.DONE);

      // pass 1: credentials out to everyone
      for (const item of pending) {
        if (this.cancelled) break;
        await this.sendCredentials(item);
        await sleep(300);
      }

      // pass 2: verdicts and registration, in the same order
      for (const item of pending) {
        if (this.cancelled) break;
        if (item.state !== BULK_STATE.TESTING) continue;
        await this.finish(item);
        await sleep(300);
      }

    } finally {

      await this.disconnect();
      this.running = false;
      this.onboarding.bulk_active = false;
      this.onboarding.is_trying_wifi = false;
      this.notify();

    }

  }

  // Puts every failed sensor back in the queue and runs again.
  async retryFailed() {

    this.items.forEach(item => {
      if (item.state === BULK_STATE.FAILED) {
        item.state = BULK_STATE.QUEUED;
        item.message = '';
      }
    });

    this.notify();
    await this.run();

  }

  // ── Pass 1 ─────────────────────────────────────────────────────────────────

  async sendCredentials(item) {

    this.setState(item, BULK_STATE.SENDING, 'Connecting...');

    const connected = await this.connect(item);

    if (!connected.success) {
      this.setState(item, BULK_STATE.FAILED, connected.message || "Couldn't connect over Bluetooth");
      return;
    }

    if (connected.sensorId && !item.sensorId) {
      item.sensorId = connected.sensorId;
    }

    this.setState(item, BULK_STATE.SENDING, 'Sending Wi-Fi...');

    const payload = { ssid: this.wifi.ssid };

    if (this.wifi.password) {
      payload.password = this.wifi.password;
    }

    const res = await this.request('POST', '/wlan', payload);

    await this.disconnect();

    if (!res.ok) {
      this.setState(item, BULK_STATE.FAILED, res.message || 'The sensor rejected the Wi-Fi settings');
      return;
    }

    if (res.parsed && (res.parsed.err == 1 || res.parsed.t === 'err')) {
      this.setState(item, BULK_STATE.FAILED, this.wlanErrorMessage(res.parsed.msg));
      return;
    }

    this.setState(item, BULK_STATE.TESTING, 'Testing the Wi-Fi...');

  }

  wlanErrorMessage(msg) {

    switch (msg) {
      case 'ssid_too_long':
        return 'The network name is too long for the sensor';
      case 'password_too_long':
        return 'The password is too long for the sensor';
      default:
        return msg ? String(msg) : 'The sensor rejected the Wi-Fi settings';
    }

  }

  // ── Pass 2 ─────────────────────────────────────────────────────────────────

  async finish(item) {

    this.setState(item, BULK_STATE.CHECKING, 'Connecting...');

    const connected = await this.connect(item);

    if (!connected.success) {
      this.setState(item, BULK_STATE.FAILED, connected.message || "Couldn't reconnect over Bluetooth");
      return;
    }

    this.setState(item, BULK_STATE.CHECKING, 'Waiting for the Wi-Fi test...');

    const status = await this.waitForStatus(item);

    if (status !== 9) {
      await this.disconnect();
      this.setState(item, BULK_STATE.FAILED, this.statusMessage(status));
      return;
    }

    this.setState(item, BULK_STATE.REGISTERING, 'Registering...');

    const acc = await this.request('POST', '/acc', {
      t: 'app',
      e: window.currentUser.email,
      p: getToken(),
      pa: getToken()
    });

    if (!acc.ok || !acc.parsed || acc.parsed.err == 1 || acc.parsed.t === 'err') {
      await this.disconnect();
      const msg = acc.parsed && acc.parsed.msg ? String(acc.parsed.msg) : (acc.message || 'Registration failed');
      this.setState(item, BULK_STATE.FAILED, /internet unavailable/i.test(msg)
        ? "The sensor couldn't check in with our server"
        : msg);
      return;
    }

    // First reading for the dashboard, then let the sensor go live.
    const readings = await this.request('GET', '/get/readings');
    item.readings = (readings.ok && readings.parsed) ? readings.parsed : null;

    await this.request('POST', '/restart', {}, true);
    await this.disconnect();

    this.setState(item, BULK_STATE.FINALIZING, 'Adding to the account...');

    const finalized = await this.apiFinalize(item);

    if (!finalized.ok) {
      this.setState(item, BULK_STATE.FAILED, finalized.message);
      return;
    }

    this.setState(item, BULK_STATE.DONE, 'Ready');

  }

  statusMessage(status) {

    switch (status) {
      case 0:
        return "Couldn't join the Wi-Fi — check the network name and password";
      case 1:
        return 'Joined the Wi-Fi but found no internet';
      case 2:
        return "Joined the Wi-Fi but couldn't reach our server";
      case -1:
        return 'Lost the sensor while it was testing';
      default:
        return 'Timed out waiting for the Wi-Fi test';
    }

  }

  // Polls /status until the sensor reaches a verdict. A sensor still at 99
  // never got the credentials (it was reset in between); hand them over again
  // once rather than waiting on nothing.
  async waitForStatus(item) {

    const started = Date.now();
    let resent = false;

    while (Date.now() - started < STATUS_TIMEOUT_MS) {

      if (this.cancelled) {
        return -2;
      }

      const res = await this.request('GET', '/status');

      if (!res.ok) {
        if (res.code === 'NOT_CONNECTED') {
          return -1;
        }
        await sleep(STATUS_POLL_MS);
        continue;
      }

      const status = parseInt(String(res.body).trim(), 10);

      if (status === 9 || status === 0 || status === 1 || status === 2) {
        return status;
      }

      if (status === 99 && !resent) {
        resent = true;
        const payload = { ssid: this.wifi.ssid };
        if (this.wifi.password) {
          payload.password = this.wifi.password;
        }
        await this.request('POST', '/wlan', payload);
      }

      await sleep(STATUS_POLL_MS);

    }

    return -3;

  }

  // What the single flow's reconnect + finish steps do for a NIST sensor:
  // record the first reading (the API also sets the calibration account's
  // 15-minute interval here), then save the name — the sensor's own ID —
  // with the intervals registration gave it.
  async apiFinalize(item) {

    const sid = item.sensorId;

    if (!sid) {
      return { ok: false, message: 'Sensor ID unknown' };
    }

    try {

      const readings = item.readings || {};

      const after = await window.apiRequest('post', `/sensor/${encodeURIComponent(sid)}/after-onboarding`, {
        tempC: readings.tempC,
        humidity: readings.humidity
      });

      if (!after || after.type !== 'success') {
        return { ok: false, message: (after && after.message) ? after.message : 'The account did not accept the sensor' };
      }

      let sendInterval = 900;

      const current = await window.apiRequest('get', `/sensor/${encodeURIComponent(sid)}`);

      if (current && current.data) {
        const parsed = parseInt(current.data.send_interval, 10);
        if (!isNaN(parsed) && parsed > 0) {
          sendInterval = parsed;
        }
      }

      const saved = await window.apiRequest('post', `/sensor/${encodeURIComponent(sid)}`, {
        sensor_name: sid,
        use_alert_interval: 0,
        send_interval: sendInterval,
        alert_interval: sendInterval
      });

      if (!saved || saved.type !== 'success') {
        return { ok: false, message: (saved && saved.message) ? saved.message : 'Could not save the sensor name' };
      }

      return { ok: true };

    } catch (e) {
      return { ok: false, message: 'Lost the internet connection while adding the sensor' };
    }

  }

  // ── Bluetooth plumbing ─────────────────────────────────────────────────────

  bridge() {
    return this.onboarding.bleBridge();
  }

  // By address first (fast, no scan); if that fails, by ID so a sensor that
  // rotated its address after a restart is still found.
  async connect(item) {

    const bridge = this.bridge();

    if (!bridge) {
      return { success: false, message: 'Bluetooth bridge not available' };
    }

    item.attempts++;

    let res = await bridge.connect({ address: item.address, sensorId: item.sensorId, timeoutMs: CONNECT_TIMEOUT_MS });

    if (!res.success && item.sensorId && !this.cancelled) {
      res = await bridge.connect({ sensorId: item.sensorId, timeoutMs: CONNECT_TIMEOUT_MS });
    }

    if (res.success && res.address) {
      item.address = res.address;
    }

    return res;

  }

  async disconnect() {

    const bridge = this.bridge();

    if (bridge) {
      try {
        await bridge.disconnect({});
      } catch (e) {
        // ignore
      }
    }

  }

  // -> { ok, status, body, parsed, code, message }
  async request(type, path, data = {}, skipResponse = false) {

    const bridge = this.bridge();

    if (!bridge) {
      return { ok: false, code: 'UNAVAILABLE', message: 'Bluetooth bridge not available' };
    }

    let res;

    try {
      res = await bridge.request({ type, path, data, skipResponse, timeoutMs: REQUEST_TIMEOUT_MS });
    } catch (e) {
      return { ok: false, code: 'EXCEPTION', message: (e && e.message) ? e.message : String(e) };
    }

    res = res || {};

    if (!(res.success == true || res.success == 'true')) {
      return { ok: false, code: res.code || 'ERROR', message: res.message || 'The sensor did not answer' };
    }

    const body = String(res.response == null ? '' : res.response).trim();
    let parsed = null;

    if (body[0] === '{' || body[0] === '[') {
      try {
        parsed = JSON.parse(body);
      } catch (e) {
        parsed = null;
      }
    }

    return { ok: true, status: res.status, body, parsed };

  }

}
